import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RedeemService } from '../redeem.service';
import { SharedService } from '../../shared/shared.service';
import { LoaderService } from '../../loader.service';
import { AuthService } from '../../auth.service';
import { ToasterService } from '../../toaster.service';
import { Toaster, ToasterType } from '../../shared/types/toaster.types';
import { Table, Pagination, TableActions, TableAction, TableData, TableDataTypes } from '../../shared/types';
import { EDIT_REDEEM } from '../../routes';

@Component({
  selector: 'app-redeem-list',
  templateUrl: './redeem-list.component.html',
  providers: [RedeemService]
})
export class RedeemListComponent implements OnInit {
  public table: Table = new Table({
    headers: {
      shift: 'Shift',
      cities: 'City',
      modeId: 'Mode',
      startDate: 'Start Date',
      createdDate: 'Created On',
      transactionCount: 'No Of Trans.',
      transactionPeriod: 'Transaction Period',
      minTransactionAmount: 'Min Transaction Amount',
      active: 'Active',
      action: 'Actions'
    },
    pagination: new Pagination(0, 50, 50, [50, 100]),
    data: []
  });

  public showFilters = true;

  public shiftSearch: string;

  public requestBody = {
    filters: {} as any,
    sort: '' as string
  };

  private cities: any[] = [];
  private shifts: any[] = [];
  private modes: any[] = [];
  private cityMap: { [displayName: string]: string } = {};
  private modeNameToId: { [name: string]: string } = {};
  private modeIdToName: { [id: string]: string } = {};
  private modesFetchedForCity = new Set<string>();
  private selectedCityNames: string[] = [];
  private selectedModeNames: string[] = [];

  private citiesFilter: any = {
    type: 'searchable-dropdown',
    title: 'City',
    values: [],
    default: [],
    key: 'redeem-city',
    initialSelected: [] as string[]
  };

  private modesFilter: any = {
    type: 'searchable-dropdown',
    title: 'Mode',
    values: [],
    default: [],
    key: 'redeem-mode',
    initialSelected: [] as string[]
  };

  private shiftsFilter: any = {
    type: 'searchable-dropdown',
    title: 'Shift',
    values: [],
    default: [],
    key: 'redeem-shift',
    initialSelected: [] as string[]
  };

  public filterComponents: any[] = [this.citiesFilter, this.modesFilter, this.shiftsFilter];

  // Columns the upstream supports sorting on (mirrors the legacy ng-table sortable columns).
  private sortableColumns = ['startDate', 'createdDate', 'active'];

  constructor(
    private redeemService: RedeemService,
    private sharedService: SharedService,
    private loaderService: LoaderService,
    private authService: AuthService,
    public toastService: ToasterService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  async ngOnInit() {
    const permissions = await this.authService.getPermissions();
    if (!permissions || permissions.indexOf('redeem_list') < 0) {
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: 'You are not authorized to access this page'
      }));
      this.router.navigate(['/']);
      return;
    }
    await this.fetchAllCities();
    const appliedLaunchFilters = await this.applyLaunchQueryParamsToFilters();
    // Launch prefill already loaded the filtered list via applyFilters.
    if (!appliedLaunchFilters) {
      await this.fetchTableData();
    }
  }

  // Prefill list filters from CSCC launch query (`city` + `mode`) and apply them.
  private async applyLaunchQueryParamsToFilters(): Promise<boolean> {
    const q = this.route.snapshot.queryParamMap;
    const cityId = (q.get('city') || '').trim();
    const modeId = (q.get('mode') || '').trim();
    if (!cityId) {
      this.showFilters = false;
      return false;
    }

    const city = this.cities.find((c: any) => String(c._id) === String(cityId));
    if (!city) {
      this.showFilters = false;
      return false;
    }
    const cityDisplayName = city.displayName;

    this.selectedCityNames = [cityDisplayName];
    this.citiesFilter = {
      ...this.citiesFilter,
      initialSelected: [cityDisplayName],
      default: [cityDisplayName]
    };
    await this.fetchModesForCitiesAsync([cityDisplayName]);

    if (!modeId) {
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: 'Mode is required in launch URL.',
      }));
      this.refreshFilterComponents();
      this.showFilters = true;
      this.applyFilters({
        'redeem-city': this.selectedCityNames,
        'redeem-mode': [],
        'redeem-shift': []
      });
      return true;
    }

    const modeName = this.modeIdToName[modeId]
      || Object.keys(this.modeNameToId).find(
        (name) => String(this.modeNameToId[name]) === String(modeId)
      )
      || '';
    if (!modeName) {
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: 'Invalid mode in launch URL for the selected city.',
      }));
      this.refreshFilterComponents();
      this.showFilters = true;
      this.applyFilters({
        'redeem-city': this.selectedCityNames,
        'redeem-mode': [],
        'redeem-shift': []
      });
      return true;
    }

    this.selectedModeNames = [modeName];
    this.modesFilter = {
      ...this.modesFilter,
      initialSelected: [modeName],
      default: [modeName]
    };
    this.fetchShiftsForCityModes(this.selectedCityNames, this.selectedModeNames);
    this.refreshFilterComponents();
    this.showFilters = true;
    this.applyFilters({
      'redeem-city': this.selectedCityNames,
      'redeem-mode': this.selectedModeNames,
      'redeem-shift': []
    });
    return true;
  }

  private refreshFilterComponents() {
    this.filterComponents = [this.citiesFilter, this.modesFilter, this.shiftsFilter];
  }

  private fetchModesForCitiesAsync(cityDisplayNames: string[]): Promise<void> {
    this.modes = [];
    this.modeNameToId = {};
    this.updateModeFilterValues([]);
    this.updateShiftFilterValues([]);
    if (!cityDisplayNames.length) {
      return Promise.resolve();
    }
    const requests = cityDisplayNames.map((displayName) => {
      const cityId = this.cityMap[displayName];
      if (!cityId) {
        return Promise.resolve();
      }
      return this.sharedService.fetchModes(cityId).toPromise().then((res: any) => {
        const modes = (res && res.data && res.data.modes) || [];
        for (const m of modes) {
          if (m && m.name && m.id != null) {
            this.modeNameToId[m.name] = m.id;
            this.modeIdToName[m.id] = m.name;
          }
        }
        this.modes = Object.keys(this.modeNameToId);
        this.updateModeFilterValues(this.modes);
      }).catch(() => undefined);
    });
    return Promise.all(requests).then(() => undefined);
  }

  async withLoader(cb) {
    try {
      this.loaderService.openLoading();
      return await cb();
    } finally {
      this.loaderService.closeLoading();
    }
  }

  async fetchAllCities() {
    const citiesRes: any = await this.withLoader(() => this.sharedService.fetchCities().toPromise());
    this.cities = (citiesRes.data.cities || []).filter((c: any) => c.active);
    this.citiesFilter.values = this.cities.map(city => city.displayName);
    this.cityMap = {};
    this.cities.forEach((city: any) => { this.cityMap[city.displayName] = city._id; });
  }

  onFilterChange($evt: any) {
    if (!$evt) {
      return;
    }
    if ('redeem-city' in $evt) {
      this.selectedCityNames = Array.isArray($evt['redeem-city']) ? $evt['redeem-city'] : [];
      this.fetchModesForCities(this.selectedCityNames);
      this.fetchShiftsForCityModes(this.selectedCityNames, this.selectedModeNames);
    }
    if ('redeem-mode' in $evt) {
      this.selectedModeNames = Array.isArray($evt['redeem-mode']) ? $evt['redeem-mode'] : [];
      this.fetchShiftsForCityModes(this.selectedCityNames, this.selectedModeNames);
    }
  }

  private fetchModesForCities(cityDisplayNames: string[]) {
    this.modes = [];
    this.modeNameToId = {};
    this.updateModeFilterValues([]);
    this.updateShiftFilterValues([]);
    if (!cityDisplayNames.length) {
      return;
    }
    for (const displayName of cityDisplayNames) {
      const cityId = this.cityMap[displayName];
      if (cityId) {
        this.sharedService.fetchModes(cityId).subscribe((res: any) => {
          const modes = (res && res.data && res.data.modes) || [];
          for (const m of modes) {
            if (m && m.name && m.id != null) {
              this.modeNameToId[m.name] = m.id;
            }
          }
          this.modes = Object.keys(this.modeNameToId);
          this.updateModeFilterValues(this.modes);
        });
      }
    }
  }

  private updateModeFilterValues(values: any[]) {
    this.modesFilter = {
      ...this.modesFilter,
      values: values as any
    };
    this.refreshFilterComponents();
  }

  private fetchShiftsForCityModes(cityDisplayNames: string[], modeNames: string[]) {
    this.shifts = [];
    if (!cityDisplayNames.length || !modeNames.length) {
      this.updateShiftFilterValues([]);
      return;
    }
    const shiftSet = new Set<string>();
    for (const cityName of cityDisplayNames) {
      const cityId = this.cityMap[cityName];
      if (!cityId) {
        continue;
      }
      for (const modeName of modeNames) {
        const modeId = this.modeNameToId[modeName];
        if (!modeId) {
          continue;
        }
        this.sharedService.fetchCaptainEarningsShifts(cityId, modeId, false).subscribe((res: any) => {
          this.extractShiftNames(res).forEach(name => shiftSet.add(name));
          this.shifts = Array.from(shiftSet);
          this.updateShiftFilterValues(this.shifts);
        });
      }
    }
  }

  private extractShiftNames(res: any): string[] {
    let arr: any[] = [];
    if (res && res.data && Array.isArray(res.data.shifts)) {
      arr = res.data.shifts;
    } else if (res && Array.isArray(res.data)) {
      arr = res.data;
    } else if (Array.isArray(res)) {
      arr = res;
    }
    return arr
      .map((s: any) => (typeof s === 'object' && s !== null) ? (s.name || '') : String(s))
      .filter((name: string) => name.trim() !== '');
  }

  private updateShiftFilterValues(values: any[]) {
    this.shiftsFilter = {
      ...this.shiftsFilter,
      values: values as any
    };
    this.refreshFilterComponents();
  }

  // Free-text shift search, mirroring the legacy `<field>__regex` ng-table column filter.
  searchByShift() {
    this.table.pagination.setPageNoToZero();
    const shift = (this.shiftSearch || '').trim();
    this.requestBody.filters = shift ? { shift__regex: shift } : {};
    this.fetchTableData();
  }

  applyFilters(filters) {
    this.table.pagination.setPageNoToZero();
    const filtersRequest: any = {};
    if ((filters['redeem-city'] || []).length > 0) {
      filtersRequest.cities__in = filters['redeem-city'].join(',');
    }

    const selectedShifts = filters['redeem-shift'] || [];
    const selectedModeIds = (filters['redeem-mode'] || [])
      .map((name: string) => this.modeNameToId[name])
      .filter((id: string) => !!id);

    if (selectedShifts.length > 0) {
      filtersRequest.shift__in = selectedShifts.join(',');
    } else if (selectedModeIds.length > 0) {
      filtersRequest.modeId__in = selectedModeIds.join(',');
    }

    this.requestBody.filters = filtersRequest;
    this.fetchTableData();
  }

  async sortByHeader(header: string) {
    const sortOrder = header.includes('-') ? '-' : '+';
    const column = header.replace('-', '');
    if (this.sortableColumns.indexOf(column) < 0) {
      return;
    }
    this.requestBody.sort = sortOrder + column;
    await this.fetchTableData();
  }

  async fetchTableData() {
    await this.withLoader(() => this.fetchRedeemRules());
  }

  async fetchRedeemRules() {
    const perPage = this.table.pagination.perPage;
    const pageNo = this.table.pagination.pageNo;

    const queryParams: any = {
      active: true,
      limit: perPage + 1,
      offset: perPage * pageNo,
      ...this.requestBody.filters
    };
    if (this.requestBody.sort) {
      queryParams.sort = this.requestBody.sort;
    }

    const permissions = await this.authService.getPermissions();
    const response: any = await this.redeemService.getRules(queryParams).toPromise();
    const fetched = (response && response.data) ? response.data : [];

    const hasNextPage = fetched.length > perPage;
    const rules = hasNextPage ? fetched.slice(0, perPage) : fetched;

    this.table.pagination.count = hasNextPage
      ? (pageNo + 1) * perPage + 1
      : pageNo * perPage + rules.length;

    const citiesWithMode = Array.from(new Set(
      rules.filter(r => r.modeId).reduce((acc, r) => acc.concat(r.cities || []), [])
    ));
    await this.ensureModeNamesForCities(citiesWithMode as string[]);

    this.table.data = rules.map(rule => ({
      shift: (rule.shift || []).join(', '),
      cities: (rule.cities || []).join(', '),
      modeId: rule.modeId ? (this.modeIdToName[rule.modeId] || rule.modeId) : '--',
      startDate: rule.startDate || '--',
      createdDate: rule.createdDate ? new Date(rule.createdDate).toISOString().slice(0, 10) : '--',
      transactionCount: rule.transactionCount,
      transactionPeriod: rule.transactionPeriod,
      minTransactionAmount: rule.minTransactionAmount,
      active: rule.active ? 'Active' : 'Deactivate',
      action: this.createAction(permissions, rule)
    }));
  }

  private async ensureModeNamesForCities(cityDisplayNames: string[]) {
    const toFetch = cityDisplayNames.filter(
      name => name && this.cityMap[name] && !this.modesFetchedForCity.has(name));
    await Promise.all(toFetch.map(async (displayName) => {
      this.modesFetchedForCity.add(displayName);
      try {
        const res: any = await this.sharedService.fetchModes(this.cityMap[displayName]).toPromise();
        const modes = (res && res.data && res.data.modes) || [];
        for (const m of modes) {
          if (m && m.id != null && m.name) {
            this.modeIdToName[m.id] = m.name;
          }
        }
      } catch (e) {
      }
    }));
  }

  private createAction(permissions: string[], rule: any) {
    const actionArr = [];
    if (permissions && permissions.indexOf('redeem_edit') >= 0) {
      actionArr.push(new TableAction({
        onClick: () => {
          this.openPage('/' + EDIT_REDEEM.replace(':id', rule._id));
        },
        text: 'Edit'
      }));
    }
    return new TableData({
      actions: new TableActions(actionArr),
      type: TableDataTypes.ACTION
    });
  }

  private openPage(url: string) {
    this.router.navigate([url]);
  }
}
